import type { ServerSortParams } from '@/hooks/useServerSort';
import { calculateClosingDisclosure } from '@/utils/calculator';
import { User } from './UserType';

export interface GetProductsRequest {
  offset?: number;
  limit?: number;
  contractStarted?: boolean;
  transactionMode?: 'purchase' | 'refinance';
}

export interface GetProductsResponse {
  products: Product[];
  isEnd: boolean;
}

export interface CreateProductRequest {
  name?: string;
  description?: string;
  price: number;
  offerPrice?: number;
  note?: string;
  photo?: string;
  photos?: string[];
  transactionMode?: 'purchase' | 'refinance';
  savedLoanType?: string;
  refinance?: ProductRefinance;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  propertyType?: string;
  monthlyPropertyTaxes?: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    fullAddress?: string;
    county?: string;
  };
}

export interface UpdateProductRequest {
  id: string;
  name?: string;
  description?: string;
  photo?: string;
  photos?: string[];
  price?: number;
  note?: string;
  transactionMode?: 'purchase' | 'refinance';
  refinance?: ProductRefinance;

  zipCode?: number;
  offerPrice?: number;
  hoaFees?: number;
  commission?: number;
  rent?: number;
  experience?: number;
  rehab?: number;
  arv?: number;
  propertyType?: string;
  monthlyPropertyTaxes?: number;
  monthlyHomeownersInsurance?: number;
  removePrepaymentPenalty?: boolean;

  // User's saved preferences for this product
  savedLoanType?: string;
  savedDownPayment?: number;

  // Address fields
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    fullAddress?: string;
    county?: string;
  };
}

export interface GetProductsByAdminRequest extends ServerSortParams {
  offset?: number;
  limit?: number;
  transactionMode?: 'purchase' | 'refinance';
  q?: string;
  userId?: string;
}

export interface GetProductsByAdminResponse {
  products: Product[];
  count: number;
}

export interface ProductLoanInfo {
  zipCode: number;
  offerPrice: number;
  hoaFees: number;
  commission: number;
  rent: number;
  experience: number;
  rehab: number;
  arv: number;
  propertyType?: string;
  monthlyPropertyTaxes?: number;
  monthlyHomeownersInsurance?: number;
  removePrepaymentPenalty?: boolean;
}

export interface ProductAddress {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  fullAddress?: string;
  county?: string;
}

export type MortgageStatementStatus = 'none' | 'uploaded' | 'reviewed';

export interface ProductMortgageStatement {
  storageKey?: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  uploadedAt?: string;
  status?: MortgageStatementStatus;
}

export interface ProductRefinanceStatement {
  escrowBalance?: number;
  buydownBalance?: number;
  prepaymentPenalty?: number;
  lastPaymentDate?: string;
  estimatedPayoff?: number;
}

/** Populated `product.contract` from API (subset of Contract). */
export interface ProductLinkedContract {
  _id?: string;
  isRatified?: boolean;
  contractStatus?: string;
}

export interface ProductRefinance {
  currentLoan?: {
    rate?: number;
    balance?: number;
    termYears?: number;
    monthlyPayment?: number;
    escrowTaxes?: number;
    escrowInsurance?: number;
    escrowHoa?: number;
  };
  property?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    fullAddress?: string;
    county?: string;
    value?: number;
    propertyType?: string;
  };
  newLoan?: {
    termYears?: number;
    creditScore?: number;
    cashOutAmount?: number;
    requestedLoanAmount?: number;
  };
  selection?: {
    program?: string;
    refiType?: string;
    occupancy?: string;
  };
  statement?: ProductRefinanceStatement;
  mortgageStatement?: ProductMortgageStatement;
}

export interface ProductListingEvent {
  date?: string;
  event?: string;
  price?: number | null;
}

export interface Product {
  _id: string;
  id?: string;
  name: string;
  price: number;
  photo: string;
  /** Soft-delete flag (backend queries filter by this). */
  isDeleted?: boolean;
  photos: string[];
  url: string;
  domain: string;
  description: string;
  note: string;
  ce_id?: string;
  loanInfo: ProductLoanInfo;
  address?: ProductAddress;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  yearBuilt?: number;
  lotSize?: number;
  propertyType?: string;
  daysOnMarket?: number;
  /** ISO date when `daysOnMarket` was last captured from a listing. */
  daysOnMarketRecordedAt?: string;
  propertyTax?: number;
  zoning?: string;
  priceChanges?: ProductListingEvent[];
  recentSales?: ProductListingEvent[];
  features?: string[];
  user?: User;
  contract?: string | ProductLinkedContract;
  contractStarted?: boolean;
  contractInProgress?: boolean;
  contractRatified?: boolean;
  createdAt: string;
  updatedAt: string;

  // User's saved preferences for this product
  savedLoanType?: string;
  savedDownPayment?: number;
  transactionMode?: 'purchase' | 'refinance';
  refinance?: ProductRefinance;
  /** Customer-run USDA rural eligibility check for this address. */
  usdaEligibility?: UsdaEligibility;
  /** Public-record lookup run by the backend when the property is added. */
  enrichment?: ProductEnrichment;
}

export type ProductEnrichmentStatus =
  | 'pending'
  | 'matched'
  | 'unmatched'
  | 'failed';

export interface ProductEnrichmentSale {
  date?: string;
  price?: number;
  qualified?: string;
  saleCode?: string;
  sourceUrl?: string;
}

export interface ProductEnrichment {
  status: ProductEnrichmentStatus;
  requestedAt?: string;
  checkedAt?: string;
  matchStatus?: string;
  normalizedAddress?: string;
  jurisdiction?: string;
  parcelId?: string;
  bedrooms?: number;
  bathrooms?: number;
  sqft?: number;
  yearBuilt?: number;
  buildingStyle?: string;
  lastSalePrice?: number;
  lastSaleDate?: string;
  salesHistory?: ProductEnrichmentSale[];
  permitLinks?: { label?: string; url?: string }[];
  /** Listing page the pipeline matched by address or listing URL. */
  listingUrl?: string;
  listingStatus?: string;
  /** A listing found at a slightly different address, waiting for the owner or an admin. */
  listingCandidate?: ProductListingCandidate;
}

export interface ProductListingCandidate {
  url: string;
  zip?: string;
  matchStatus?: string;
  listPrice?: number;
  photoUrls?: string[];
  rejectedAt?: string;
}

export type UsdaEligibilityStatus = 'eligible' | 'ineligible' | 'unknown';

export interface UsdaEligibility {
  status: UsdaEligibilityStatus;
  matchedAddress?: string;
  checkedAt?: string;
  source?: string;
}

export interface UsdaEligibilityResult extends UsdaEligibility {
  lookupUrl: string;
  message?: string;
}

export type ProductEsitmation = ReturnType<typeof calculateClosingDisclosure>;

export enum PRODUCT_PRICE_MATCH_STATUS {
  AFFORDABLE = 'Affordable',
  CLOSE_MATCH = 'Close Match',
  NOT_AFFORDABLE = 'Not Affordable',
}

export enum PRODUCT_QUALIFY_STATUS {
  QUALIFIED = 'Qualified',
  UNQUALIFIED = 'Unqualified',
}
export enum PRODUCT_APPROVE_STATUS {
  APPROVED = 'Approved',
  NOT_APPROVED = 'Not Approved',
}
